// InspirationsView.tsx — fourth tab of /content-map.
//
// v2 (Erez, 2026-06-10 evening): a VISUAL LIBRARY — the actual collected
// assets (diagrams, post visuals) laid out in a grid like the Backlog,
// sectioned by source. Image cards show the asset itself (contained, never
// cropped — diagrams must stay readable); the thin strip below carries the
// topic and a link to the source page. Entries without an image (the
// text-described swipe-file rows) fall back to a compact text card in a
// closing "directory" section.

import { useEffect, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

type InspirationPost = {
  id: string;
  creator: string;
  platform: string;
  account_handle: string | null;
  account_url: string | null;
  post_url: string | null;
  post_text: string;
  topic: string | null;
  posted_at_approx: string | null;
  why_it_works: string | null;
  image_url: string | null;
  source_page: string | null;
  created_at: string;
};

function AssetCard({ p }: { p: InspirationPost }) {
  const href = p.source_page ?? p.post_url ?? p.image_url ?? undefined;
  return (
    <div
      style={{
        background: T.white,
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <a
        href={p.image_url ?? href}
        target="_blank"
        rel="noreferrer"
        style={{
          display: "block",
          aspectRatio: "1 / 1",
          background: "#FFFFFF",
        }}
      >
        <img
          src={p.image_url!}
          alt={p.post_text}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            padding: 8,
            boxSizing: "border-box",
          }}
        />
      </a>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 12px",
        }}
      >
        {p.topic && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: T.ink2,
              background: T.bg2,
              borderRadius: 999,
              padding: "2px 8px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {p.topic}
          </span>
        )}
        <span
          style={{
            fontSize: 11,
            color: T.ink3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
          }}
        >
          {p.post_text}
        </span>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label="Open source page"
            style={{ display: "inline-flex", color: T.skyDark, flexShrink: 0 }}
          >
            <ExternalLink style={{ width: 12, height: 12 }} />
          </a>
        )}
      </div>
    </div>
  );
}

function TextCard({ p }: { p: InspirationPost }) {
  return (
    <div
      style={{
        background: T.white,
        borderRadius: 10,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{p.creator}</span>
        {p.topic && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: T.ink2,
              background: T.bg2,
              borderRadius: 999,
              padding: "2px 8px",
            }}
          >
            {p.topic}
          </span>
        )}
        {(p.post_url ?? p.account_url) && (
          <a
            href={p.post_url ?? p.account_url ?? undefined}
            target="_blank"
            rel="noreferrer"
            aria-label="Open"
            style={{ marginLeft: "auto", display: "inline-flex", color: T.skyDark }}
          >
            <ExternalLink style={{ width: 12, height: 12 }} />
          </a>
        )}
      </div>
      <p style={{ fontSize: 12, color: T.ink2, lineHeight: 1.5, margin: 0 }}>
        {p.post_text}
      </p>
      {p.why_it_works && (
        <p
          style={{
            fontSize: 11.5,
            color: T.ink3,
            fontStyle: "italic",
            margin: "6px 0 0",
            lineHeight: 1.45,
          }}
        >
          Steal: {p.why_it_works}
        </p>
      )}
    </div>
  );
}

export function InspirationsView() {
  const api = useApi();
  const [posts, setPosts] = useState<InspirationPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/inspirations");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { posts: InspirationPost[] };
        if (!cancelled) setPosts(json.posts);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div
        style={{
          padding: "16px 20px",
          color: T.red,
          background: T.redSoft,
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        Failed to load inspirations: {error}
      </div>
    );
  }

  if (posts === null) {
    return (
      <div
        style={{
          padding: 24,
          fontSize: 14,
          color: T.ink3,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
        Loading…
      </div>
    );
  }

  const visual = posts.filter((p) => p.image_url);
  const textOnly = posts.filter((p) => !p.image_url);

  // Visual sections grouped by creator/source.
  const byCreator = new Map<string, InspirationPost[]>();
  for (const p of visual) {
    const list = byCreator.get(p.creator) ?? [];
    list.push(p);
    byCreator.set(p.creator, list);
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: T.skyDark,
            marginBottom: 8,
          }}
        >
          MyJarvis · Marketing
        </div>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: T.ink,
            margin: 0,
            letterSpacing: "-0.02em",
          }}
        >
          Inspirations
        </h1>
        <p style={{ fontSize: 14, color: T.ink2, maxWidth: 720, lineHeight: 1.6 }}>
          A visual library of collected assets — diagrams and post visuals
          from the publishers we study, gathered by research agents. Click any
          asset for the full image; the arrow opens its source.
        </p>
      </div>

      {visual.length === 0 && (
        <div
          style={{
            padding: "18px 20px",
            background: T.bg2,
            borderRadius: 10,
            fontSize: 13,
            color: T.ink2,
            marginBottom: 28,
          }}
        >
          The collectors are out — visual assets land here as they return.
        </div>
      )}

      {[...byCreator.entries()].map(([creator, list]) => (
        <section key={creator} style={{ marginBottom: 36 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: `2px solid ${T.ink}`,
            }}
          >
            <h2
              style={{
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: T.ink,
                margin: 0,
              }}
            >
              {creator}
            </h2>
            <span style={{ fontSize: 11, color: T.ink3, fontWeight: 600 }}>
              {list.length} assets
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {list.map((p) => (
              <AssetCard key={p.id} p={p} />
            ))}
          </div>
        </section>
      ))}

      {textOnly.length > 0 && (
        <section style={{ marginBottom: 36 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: `2px solid ${T.ink}`,
            }}
          >
            <h2
              style={{
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: T.ink,
                margin: 0,
              }}
            >
              Directory — publishers to study
            </h2>
            <span style={{ fontSize: 11, color: T.ink3, fontWeight: 600 }}>
              {textOnly.length} entries
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 12,
            }}
          >
            {textOnly.map((p) => (
              <TextCard key={p.id} p={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
